import React from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export const STEPS = [
  { key: "understand", label: "Understand" },
  { key: "design", label: "Design" },
  { key: "calculate", label: "Calculate" },
  { key: "propose", label: "Propose" },
  { key: "send", label: "Send" },
];

export function stepIndex(step) {
  const i = STEPS.findIndex((s) => s.key === step);
  return i < 0 ? 0 : i;
}

/**
 * Guided flow as a quiet segmented control.
 * `reached` = highest step the data supports (clickable up to there).
 * `dense`   = hide labels of non-current steps below xl (dots only).
 */
export function StepRail({ step, reached, onSelect, dense, className }) {
  const cur = stepIndex(step);
  const max = stepIndex(reached);
  return (
    <ol className={cn("inline-flex items-center gap-0.5 rounded-full bg-black/[0.045] p-1", className)} aria-label="Workspace steps" data-testid="workspace-step-rail">
      {STEPS.map((s, i) => {
        const state = i < cur ? "done" : i === cur ? "current" : "todo";
        const enabled = i <= max;
        return (
          <li key={s.key}>
            <button
              type="button"
              disabled={!enabled}
              onClick={() => enabled && onSelect?.(s.key)}
              data-testid={`workspace-step-${s.key}`}
              aria-current={state === "current" ? "step" : undefined}
              aria-label={`${i + 1}. ${s.label}`}
              title={s.label}
              className={cn(
                "flex h-8 items-center gap-2 rounded-full px-3 text-[12.5px] font-medium transition-[color,background-color,box-shadow] duration-150 focus-visible:ring-2 focus-visible:ring-ring/40",
                state === "current" && "bg-white text-foreground shadow-[0_1px_2px_hsl(30_12%_18%/0.08),0_0_0_1px_hsl(30_12%_18%/0.05)]",
                state === "done" && "text-foreground/70 hover:bg-white/70 hover:text-foreground",
                state === "todo" && "text-muted-foreground/55",
                !enabled && "cursor-default",
                enabled && state !== "current" && "cursor-pointer"
              )}
            >
              {state === "done" ? (
                <Check className="h-3 w-3 text-primary" strokeWidth={3} />
              ) : (
                <span className={cn("h-1.5 w-1.5 rounded-full", state === "current" ? "bg-primary" : "bg-black/[0.16]")} />
              )}
              <span className={cn(dense && state !== "current" && "hidden xl:inline")}>{s.label}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
